import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Copy, ExternalLink, Link2, LogOut, TicketPercent } from "lucide-react";

const formatDateTime = (value) => (value ? new Date(value).toLocaleString("pt-BR") : "-");
const formatPercentLabel = (value) => (Number(value || 0) > 0 ? `${Number(value)}%` : "Sem desconto");

const AccountantDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newLink, setNewLink] = useState({ client_name: "", client_email: "", notes: "" });

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/accountants/dashboard`, { withCredentials: true });
      setDashboard(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao carregar dashboard do contador");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleCreateLink = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await axios.post(`${API}/accountants/links`, newLink, { withCredentials: true });
      toast.success("Link de parceria criado com sucesso");
      setNewLink({ client_name: "", client_email: "", notes: "" });
      if (response.data?.link?.public_url) {
        await navigator.clipboard.writeText(response.data.link.public_url);
        toast.success("Link copiado para a área de transferência");
      }
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar link");
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (value) => {
    await navigator.clipboard.writeText(value);
    toast.success("Link copiado");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy" />
      </div>
    );
  }

  const profile = dashboard?.profile || {};
  const quota = dashboard?.quota_summary || {};
  const links = dashboard?.links || [];
  const diagnostics = dashboard?.diagnostics || [];

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/accountant" className="flex items-center">
            <BrandLogo size="md" className="max-w-[220px]" />
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600 hidden sm:block">Olá, {user?.name?.split(" ")[0]}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <div>
          <h1 className="text-3xl font-serif font-bold text-navy">Dashboard do Contador Parceiro</h1>
          <p className="text-slate-600 mt-2">
            Gere links, acompanhe diagnósticos vinculados e visualize a progressão dos benefícios da parceria.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card><CardContent className="p-5"><p className="text-sm text-slate-500">Grátis restantes</p><p className="mt-2 text-3xl font-bold text-emerald-600">{quota.free_remaining ?? 0}</p></CardContent></Card>
          <Card><CardContent className="p-5"><p className="text-sm text-slate-500">50% restantes</p><p className="mt-2 text-3xl font-bold text-amber-600">{quota.half_remaining ?? 0}</p></CardContent></Card>
          <Card><CardContent className="p-5"><p className="text-sm text-slate-500">Links ativos</p><p className="mt-2 text-3xl font-bold text-navy">{quota.active_links ?? 0}</p></CardContent></Card>
          <Card><CardContent className="p-5"><p className="text-sm text-slate-500">Próximo benefício</p><p className="mt-2 text-3xl font-bold text-sky-600">{formatPercentLabel(quota.current_discount_percent)}</p></CardContent></Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-[420px,1fr]">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="font-serif text-xl">Gerar link de parceria</CardTitle>
              <CardDescription>
                O sistema aplica automaticamente 100%, depois 50% e, após isso, cria o vínculo sem desconto.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateLink} className="space-y-4">
                <div>
                  <Label>Nome do cliente</Label>
                  <Input value={newLink.client_name} onChange={(e) => setNewLink({ ...newLink, client_name: e.target.value })} />
                </div>
                <div>
                  <Label>E-mail do cliente</Label>
                  <Input type="email" value={newLink.client_email} onChange={(e) => setNewLink({ ...newLink, client_email: e.target.value })} />
                </div>
                <div>
                  <Label>Observações</Label>
                  <Input value={newLink.notes} onChange={(e) => setNewLink({ ...newLink, notes: e.target.value })} />
                </div>
                <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
                  <p>Escritório: {profile.company_name || "Não informado"}</p>
                  <p>CRC: {profile.crc || "Não informado"}</p>
                </div>
                <Button type="submit" className="w-full bg-navy hover:bg-navy/90" disabled={creating}>
                  <Link2 className="h-4 w-4 mr-2" />
                  {creating ? "Gerando..." : "Gerar novo link"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="font-serif text-xl">Links gerados</CardTitle>
              <CardDescription>Controle dos acessos liberados para seus clientes.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {links.length === 0 ? (
                <p className="text-sm text-slate-500">Nenhum link gerado até o momento.</p>
              ) : (
                links.slice(0, 12).map((link) => (
                  <div key={link.partnership_link_id} className="rounded-lg border p-4 space-y-3">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                      <div>
                        <p className="font-semibold text-navy">{link.client_name || "Cliente não informado"}</p>
                        <p className="text-xs text-slate-500">{link.client_email || "Sem e-mail informado"}</p>
                      </div>
                      <div className="flex gap-2">
                        <Badge className="bg-sky-100 text-sky-800">{formatPercentLabel(link.discount_percent)}</Badge>
                        <Badge variant="outline">{link.status}</Badge>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 break-all">{link.public_url}</div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => copyLink(link.public_url)}>
                        <Copy className="h-4 w-4 mr-1" />
                        Copiar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => window.open(link.public_url, "_blank", "noopener,noreferrer")}>
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Abrir
                      </Button>
                      {link.coupon_code && (
                        <Badge className="bg-emerald-100 text-emerald-800">
                          <TicketPercent className="h-3 w-3 mr-1" />
                          {link.coupon_code}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">Criado em {formatDateTime(link.created_at)}</p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="font-serif text-xl">Diagnósticos vinculados</CardTitle>
            <CardDescription>Resultados e pagamentos que entraram pela sua parceria.</CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {diagnostics.length === 0 ? (
              <p className="text-sm text-slate-500">Ainda não há diagnósticos vinculados aos seus links.</p>
            ) : (
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <th className="py-3 pr-3">Empresa</th>
                    <th className="py-3 pr-3">Cliente</th>
                    <th className="py-3 pr-3">Benefício</th>
                    <th className="py-3 pr-3">Status</th>
                    <th className="py-3 pr-3">Nota</th>
                    <th className="py-3 pr-3">Pagamento</th>
                    <th className="py-3 pr-3">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {diagnostics.map((item) => (
                    <tr key={item.diagnostic_id} className="border-b">
                      <td className="py-3 pr-3">{item.company_name}</td>
                      <td className="py-3 pr-3">
                        <div>{item.client_name}</div>
                        <div className="text-xs text-slate-500">{item.client_email}</div>
                      </td>
                      <td className="py-3 pr-3">{formatPercentLabel(item.discount_percent)}</td>
                      <td className="py-3 pr-3">{item.status || "-"}</td>
                      <td className="py-3 pr-3">{item.overall_score != null ? Number(item.overall_score).toFixed(2) : "-"}</td>
                      <td className="py-3 pr-3">{item.payment?.status_label || "-"}</td>
                      <td className="py-3 pr-3">
                        <div className="flex gap-2">
                          <Link to={`/payment/diagnostic/${item.diagnostic_id}`}>
                            <Button size="sm" variant="outline">Pagamento</Button>
                          </Link>
                          {item.status === "submitted" && (
                            <Link to={`/results/${item.diagnostic_id}`}>
                              <Button size="sm" variant="outline">Resultado</Button>
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AccountantDashboard;
